import { useAuth } from '@/context/AuthContext'
import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import styles from './DashboardPage.module.css'
import logo from '@/assets/logo.jpg'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

interface UserStats {
  active_conversations: number
  days_on_platform: number
  sessions_completed: number
}

export default function DashboardPage() {
  const { user, logout } = useAuth()
  const [stats, setStats] = useState<UserStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  // Fetch user statistics on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch(`${API_BASE_URL}/users/stats/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          setStats(data)
        } else {
          console.error('Failed to fetch user stats')
        }
      } catch (error) {
        console.error('Error fetching stats:', error)
      } finally {
        setStatsLoading(false)
      }
    }

    fetchStats()
  }, [])

  const getInitials = () => {
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase()
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const dailyAffirmations = [
    "You are worthy of love, support, and healing. 💜",
    "Every step forward is progress, no matter how small. 🌱",
    "It's okay to not be okay. Healing takes time. 🌸",
    "You have the inner strength to face today's challenges. 💪",
    "Your mental health matters. You matter. ⭐",
    "Be gentle with yourself—you're doing better than you think. 🌈",
    "Today is a new opportunity for growth and peace. 🌻",
    "You deserve happiness, peace, and fulfillment. ✨"
  ]

  const getRandomAffirmation = () => {
    const index = new Date().getDate() % dailyAffirmations.length
    return dailyAffirmations[index]
  }

  const wellnessTips = [
    {
      icon: '🧘',
      title: '5-Minute Breathing',
      description: 'Try box breathing: inhale 4 seconds, hold 4, exhale 4, hold 4. Repeat 4 times.'
    },
    {
      icon: '💧',
      title: 'Hydration Check',
      description: 'Have you had water recently? Hydration directly impacts mental clarity and mood.'
    },
    {
      icon: '🚶',
      title: 'Movement Break',
      description: 'Even a 10-minute walk can boost endorphins and reduce stress hormones.'
    },
    {
      icon: '🙏',
      title: 'Gratitude Moment',
      description: 'Name three things you\'re grateful for today. Gratitude shifts focus to the positive.'
    }
  ]

  const quickStats = [
    {
      icon: '💬',
      label: 'Active Conversations',
      value: statsLoading ? '...' : (stats?.active_conversations.toString() || '0')
    },
    {
      icon: '📅',
      label: 'Days on Platform',
      value: statsLoading ? '...' : (stats?.days_on_platform.toString() || '0')
    },
    {
      icon: '⭐',
      label: 'Sessions Completed',
      value: statsLoading ? '...' : (stats?.sessions_completed.toString() || '0')
    }
  ]

  const actionCards = [
    {
      to: '/chat',
      icon: '💬',
      title: 'Start Conversation',
      description: 'Talk to our AI assistant in a safe, confidential space. Get empathetic support for whatever you\'re experiencing.',
      cta: 'Begin Chat →',
      primary: true
    },
    {
      to: '/assessments',
      icon: '📊',
      title: 'Wellness Assessments',
      description: 'Take clinically-informed assessments to understand your mental health. Get personalized reports from our psychologists.',
      cta: 'Take Assessment →',
      primary: false
    },
    {
      to: '/conversations',
      icon: '📝',
      title: 'Conversation History',
      description: 'Review your past conversations, track your therapeutic journey, and revisit helpful insights.',
      cta: 'View History →',
      primary: false
    },
    {
      to: '/profile',
      icon: '👤',
      title: 'My Profile',
      description: 'Manage your account settings, update preferences, and personalize your wellness experience.',
      cta: 'Edit Profile →',
      primary: false
    },
    {
      to: '/resources',
      icon: '📚',
      title: 'Wellness Resources',
      description: 'Access mental health articles, breathing exercises, self-help tools, and crisis support information.',
      cta: 'Explore Resources →',
      primary: false
    }
  ]

  const infoItems = [
    {
      icon: '🤖',
      title: 'AI-Powered Support',
      description: 'Chat with our compassionate AI trained by mental health professionals'
    },
    {
      icon: '🚨',
      title: 'Crisis Detection',
      description: 'Automatic detection of urgent situations with immediate crisis resources'
    },
    {
      icon: '👨‍⚕️',
      title: 'Specialist Matching',
      description: 'Get matched with licensed mental health professionals when needed'
    },
    {
      icon: '🔒',
      title: '100% Confidential',
      description: 'Your conversations are private and protected with end-to-end encryption'
    }
  ]

  return (
    <div className={styles.dashboardContainer}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.logoSection}>
          <Link to="/dashboard" className={styles.logo}>
            <img src={logo} alt="ShwetaHeals" className={styles.logoImage} />
            <span className={styles.logoText}>ShwetaHeals</span>
          </Link>
        </div>
        <div className={styles.welcomeSection}>
          <div className={styles.welcomeContent}>
            <h1 className={styles.welcomeTitle}>
              {getGreeting()}, {user?.first_name || 'there'}!
            </h1>
            <p className={styles.subtitle}>
              Welcome to your mental wellness dashboard. How are you feeling today?
            </p>
            <div className={styles.affirmation}>
              <span className={styles.affirmationIcon}>✨</span>
              <span className={styles.affirmationText}>{getRandomAffirmation()}</span>
            </div>
          </div>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {getInitials()}
            </div>
            <button className={styles.logoutButton} onClick={logout}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className={styles.statsGrid}>
        {quickStats.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={styles.statIcon}>{stat.icon}</div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{stat.value}</span>
              <span className={styles.statLabel}>{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Action Cards Grid */}
      <div className={styles.actionsGrid}>
        {actionCards.map((card, index) => (
          <Link
            key={index}
            to={card.to}
            className={`${styles.actionCard} ${card.primary ? styles.primary : ''}`}
          >
            <div className={styles.actionIcon}>{card.icon}</div>
            <h3 className={styles.actionTitle}>{card.title}</h3>
            <p className={styles.actionDescription}>{card.description}</p>
            <span className={styles.actionCta}>{card.cta}</span>
          </Link>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className={styles.twoColumnLayout}>
        {/* Wellness Tips */}
        <div className={styles.wellnessTips}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.sectionIcon}>💡</div>
            Daily Wellness Tips
          </h2>
          <div className={styles.tipsList}>
            {wellnessTips.map((tip, index) => (
              <div key={index} className={styles.tipCard}>
                <div className={styles.tipIcon}>{tip.icon}</div>
                <div className={styles.tipContent}>
                  <h4 className={styles.tipTitle}>{tip.title}</h4>
                  <p className={styles.tipDescription}>{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How We Help */}
        <div className={styles.infoSection}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.sectionIcon}>🌟</div>
            How Shweta Heals Helps
          </h2>
          <ul className={styles.infoList}>
            {infoItems.map((item, index) => (
              <li key={index} className={styles.infoItem}>
                <div className={styles.infoItemIcon}>{item.icon}</div>
                <span className={styles.infoItemText}>
                  <strong>{item.title}</strong>
                  {item.description}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Emergency Resources Banner */}
      <div className={styles.emergencyBanner}>
        <div className={styles.emergencyContent}>
          <div className={styles.emergencyIcon}>🆘</div>
          <div className={styles.emergencyText}>
            <strong>In Crisis? Help is Available 24/7</strong>
            If you're having thoughts of self-harm or need immediate support, please reach out.
          </div>
          <div className={styles.emergencyNumbers}>
            <a href="tel:112" className={styles.emergencyLink}>📞 Emergency: 112</a>
            <a href="tel:1800599099" className={styles.emergencyLink}>📱 iCALL: 1800-599-099</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        © 2025 Shweta Heals — Your Mental Wellness Partner
      </div>
    </div>
  )
}
